// Phase B.3 — Compact "Station Clock" widget for the sidebar.
//
// Shows the current America/Chicago time (live, ticked once per second) and
// the soonest upcoming active prayer slot. Read-only — admins manage slots
// from the dashboard's Schedule Manager.
//
// Time rendering is done CLIENT-SIDE in the browser using
// `toLocaleTimeString("en-US", { timeZone: "America/Chicago" })` so the
// widget keeps ticking even if /api/schedule/next polls fail.

import React, { useEffect, useRef, useState } from "react";
import { Clock, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const POLL_MS = 60 * 1000; // 1 minute — slot list changes rarely

function chicagoNowString() {
  try {
    return new Date().toLocaleTimeString("en-US", {
      timeZone: "America/Chicago",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

function formatFiresIn(minutes) {
  if (minutes == null) return "";
  if (minutes < 1) return "now";
  if (minutes < 60) return `in ${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  if (h < 24) return m > 0 ? `in ${h}h ${m}m` : `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

export default function StationClock() {
  const [nowStr, setNowStr] = useState(chicagoNowString());
  const [next, setNext] = useState(null); // { slot, fires_in_minutes, weekday_label }
  const [loaded, setLoaded] = useState(false);
  const pollRef = useRef(null);

  // Tick the clock display once per second.
  useEffect(() => {
    const id = setInterval(() => setNowStr(chicagoNowString()), 1000);
    return () => clearInterval(id);
  }, []);

  // Poll the schedule.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/schedule/next");
        if (cancelled) return;
        setNext(data?.next || null);
      } catch (_) { /* surface nothing — clock keeps ticking */ }
      finally { if (!cancelled) setLoaded(true); }
    };
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  return (
    <div
      className="rounded-lg border border-[#22262E] bg-[#0E1014]/60 px-3 py-2.5"
      data-testid="station-clock"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={1.5} />
          <span className="overline text-[10px] text-[#A1A4AB]">Station Clock</span>
        </div>
        <div className="overline text-[10px] text-[#6C707A]">CT</div>
      </div>
      <div
        className="font-serif text-[#FDFBF7] text-2xl tracking-tight tabular-nums leading-none"
        data-testid="station-clock-time"
      >
        {nowStr || "--:--"}
      </div>
      {loaded && (
        next ? (
          <div className="mt-2 flex items-center gap-1.5 text-[11px]" data-testid="station-clock-next">
            <ChevronRight className="w-3 h-3 text-[#C5A059] flex-shrink-0" strokeWidth={2} />
            <span className="text-[#C5A059] truncate">{next.slot?.label || "Next slot"}</span>
            <span className="text-[#6C707A] whitespace-nowrap ml-auto">
              {next.slot?.time_chicago} · {formatFiresIn(next.fires_in_minutes)}
            </span>
          </div>
        ) : (
          <div className="mt-2 text-[11px] text-[#6C707A]" data-testid="station-clock-empty">
            No upcoming slots
          </div>
        )
      )}
    </div>
  );
}
